import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, ArrowRight, Video, BarChart3, UserCheck,
  FileCheck, Globe, Zap, Menu, X, ChevronRight,
} from "lucide-react";
import vexisLogo from "@assets/vexis-logo-2026.jpg";

const INDIGO = "#6366f1";
const DARK_BG = "#05070a";
const CARD_BORDER = "rgba(99,102,241,0.18)";
const MUTED = "#94a3b8";
const DIMMED = "#475569";

const SUPPORTING_CARDS = [
  {
    title: "Role-specific questions",
    body: "Every assessment is built around what the role actually requires — not a generic question library.",
  },
  {
    title: "Human review on every submission",
    body: "A trained reviewer completes a written assessment of every candidate before any outcome is set. No automated rejections.",
  },
  {
    title: "Dossier written to your ATS automatically",
    body: "The result lands in your candidate record. Your client gets a secure link. Nothing to format, nothing to chase.",
  },
];

const FEATURES = [
  {
    icon: <Globe className="w-5 h-5" />,
    title: "ATS integration via OAuth",
    body: "Connect Vexis to your ATS in under two minutes. One action button appears on every candidate record.",
  },
  {
    icon: <Video className="w-5 h-5" />,
    title: "Structured video assessment",
    body: "Candidates complete a role-specific video assessment in under 15 minutes. Any device. No app. No scheduling.",
  },
  {
    icon: <BarChart3 className="w-5 h-5" />,
    title: "AI triage — Green, Amber, Red",
    body: "Every submission is automatically scored and banded. Your review time goes where it matters.",
  },
  {
    icon: <UserCheck className="w-5 h-5" />,
    title: "Human reviewer on every candidate",
    body: "Every candidate is reviewed by a person before any outcome is set. Your clients never see a raw AI output.",
  },
  {
    icon: <FileCheck className="w-5 h-5" />,
    title: "Boardroom-ready dossier",
    body: "Executive summary, role fit analysis, competency ratings, key interview moments. Ready to send to a client in two minutes.",
  },
  {
    icon: <Zap className="w-5 h-5" />,
    title: "Your own brand, your own domain",
    body: "Candidates receive communications from your address. Dossiers carry your agency name. Vexis is your infrastructure, not your identity.",
  },
];

export default function Start() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Start Your Agency | Vexis";
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: DARK_BG }}>

      {/* Nav */}
      <nav
        className="sticky top-0 z-50"
        style={{ backgroundColor: "rgba(5,7,10,0.92)", borderBottom: "1px solid rgba(99,102,241,0.15)", backdropFilter: "blur(20px)" }}
      >
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link href="/" className="flex items-center gap-2">
            <img src={vexisLogo} alt="VEXIS Talent Agency" className="h-10 md:h-12 w-auto" />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {[
              { href: "/", label: "Home" },
              { href: "/pricing", label: "Pricing" },
              { href: "/about", label: "About Vexis" },
            ].map(({ href, label }) => (
              <Link key={label} href={href} className="text-sm font-medium transition-colors" style={{ color: MUTED }}>
                {label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex gap-3">
            <Link href="/login">
              <Button variant="ghost" className="font-medium text-slate-300 hover:text-white hover:bg-white/10">
                Sign In
              </Button>
            </Link>
            <Link href="/contact">
              <Button
                className="font-semibold"
                style={{ backgroundColor: INDIGO, color: "#ffffff" }}
                onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = "#5558e3")}
                onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = INDIGO)}
                data-testid="button-nav-enquiry"
              >
                Make an Enquiry
              </Button>
            </Link>
          </div>

          <button
            className="lg:hidden p-2"
            style={{ color: "#ffffff" }}
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label="Toggle menu"
            data-testid="button-mobile-menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div style={{ backgroundColor: DARK_BG, borderTop: "1px solid rgba(99,102,241,0.15)" }}>
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {[
                { href: "/", label: "Home" },
                { href: "/pricing", label: "Pricing" },
                { href: "/about", label: "About Vexis" },
                { href: "/contact", label: "Make an Enquiry" },
              ].map(({ href, label }) => (
                <Link
                  key={label}
                  href={href}
                  className="text-sm font-medium py-2"
                  style={{ color: MUTED }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* SECTION 1 — Hero */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 80% 70% at 50% 0%, rgba(99,102,241,0.14) 0%, transparent 70%)" }}
        />
        <div className="container mx-auto px-4 text-center relative z-10">
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 text-xs font-semibold uppercase tracking-widest"
            style={{ border: "1px solid rgba(99,102,241,0.35)", background: "rgba(99,102,241,0.08)", color: "#a5b4fc" }}
            data-testid="badge-hero"
          >
            For New and Independent Recruiters
          </div>

          <h1
            className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight max-w-4xl mx-auto"
            style={{
              background: "linear-gradient(to bottom, #ffffff 0%, #c7d2fe 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
            data-testid="heading-hero"
          >
            Launch your agency with enterprise-grade assessment from day one.
          </h1>

          <p className="text-base md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed" style={{ color: MUTED }}>
            Most new agencies spend years building the credibility that comes from a structured, consistent assessment process. Vexis gives you that from your first placement.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-5">
            <a href="/#how-it-works">
              <Button
                size="lg"
                variant="outline"
                className="font-semibold px-8 border-white/20 text-white hover:bg-white/10"
                data-testid="button-hero-see-platform"
              >
                See the platform
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link href="/contact">
              <Button
                size="lg"
                className="font-semibold px-8"
                style={{ backgroundColor: INDIGO, color: "#ffffff" }}
                onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = "#5558e3")}
                onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = INDIGO)}
                data-testid="button-hero-enquiry"
              >
                Make an Enquiry
              </Button>
            </Link>
          </div>
          <p className="text-xs" style={{ color: DIMMED }}>
            No setup fees. No per-hire costs. Connect your ATS and start.
          </p>
        </div>
      </section>

      {/* Separator */}
      <div className="h-px" style={{ background: "linear-gradient(to right, transparent, rgba(99,102,241,0.25), transparent)" }} />

      {/* SECTION 2 — The problem you're solving */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2
            className="text-2xl md:text-4xl font-bold mb-6 text-center leading-tight"
            style={{ color: "#ffffff" }}
            data-testid="heading-problem"
          >
            Your clients don't want a list. They want a decision.
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-center mb-12" style={{ color: MUTED }}>
            When you send a client five CVs and a ranking, you're handing them a problem. They have to read, interpret, and decide — and if it goes wrong, it reflects on you. Vexis gives every candidate you send a boardroom-ready dossier: video assessment, scored against the role, human-reviewed, and formatted for the people making the hiring decision. Your client reads it in two minutes and makes a call with confidence.
          </p>

          <div className="grid md:grid-cols-3 gap-5">
            {SUPPORTING_CARDS.map(card => (
              <div
                key={card.title}
                className="rounded-2xl p-6 transition-all duration-300"
                style={{ border: `1px solid ${CARD_BORDER}`, background: "rgba(255,255,255,0.03)" }}
                data-testid={`card-${card.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center mb-4"
                  style={{ background: "rgba(99,102,241,0.15)" }}
                >
                  <CheckCircle2 className="w-4 h-4" style={{ color: "#818cf8" }} />
                </div>
                <h3 className="font-semibold mb-2" style={{ color: "#ffffff" }}>{card.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: MUTED }}>{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Separator */}
      <div className="h-px" style={{ background: "linear-gradient(to right, transparent, rgba(99,102,241,0.25), transparent)" }} />

      {/* SECTION 3 — What you get from day one */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2
              className="text-2xl md:text-4xl font-bold mb-4"
              style={{ color: "#ffffff" }}
              data-testid="heading-features"
            >
              What Vexis gives an independent recruiter
            </h2>
            <p className="text-base md:text-lg" style={{ color: MUTED }}>
              The infrastructure of an established agency. Without building any of it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {FEATURES.map(f => (
              <div
                key={f.title}
                className="flex gap-4 rounded-2xl p-6 transition-all duration-300 group"
                style={{ border: `1px solid ${CARD_BORDER}`, background: "rgba(255,255,255,0.02)" }}
                data-testid={`feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: "rgba(99,102,241,0.12)", color: "#818cf8" }}
                >
                  {f.icon}
                </div>
                <div>
                  <h3 className="font-semibold mb-1.5" style={{ color: "#ffffff" }}>{f.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: MUTED }}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Separator */}
      <div className="h-px" style={{ background: "linear-gradient(to right, transparent, rgba(99,102,241,0.25), transparent)" }} />

      {/* SECTION 4 — Pricing context */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <h2
            className="text-2xl md:text-3xl font-bold mb-5"
            style={{ color: "#ffffff" }}
            data-testid="heading-pricing"
          >
            Start free. Scale when you're ready.
          </h2>
          <p className="text-base leading-relaxed mb-6" style={{ color: MUTED }}>
            The Free plan covers everything you need to run your first assessments. No setup fee, no per-hire charge, no lock-in. When your volume grows, move to a paid plan. When your clients want white label, move to Enterprise.
          </p>
          <Link
            href="/pricing"
            className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
            style={{ color: "#818cf8" }}
            data-testid="link-pricing"
          >
            See full pricing
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* SECTION 5 — Bottom CTA */}
      <section
        className="relative py-20 md:py-28 overflow-hidden"
        style={{ background: "linear-gradient(135deg, rgba(5,7,10,1) 0%, rgba(20,18,60,0.9) 50%, rgba(5,7,10,1) 100%)" }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(99,102,241,0.16) 0%, transparent 70%)" }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ borderTop: "1px solid rgba(99,102,241,0.2)", borderBottom: "1px solid rgba(99,102,241,0.2)" }}
        />
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2
            className="text-2xl md:text-4xl font-bold mb-4"
            style={{
              background: "linear-gradient(to bottom, #ffffff 0%, #c7d2fe 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
            data-testid="heading-bottom-cta"
          >
            Ready to build something worth having?
          </h2>
          <p className="text-base max-w-xl mx-auto mb-8 leading-relaxed" style={{ color: MUTED }}>
            Tell us about your agency — new or established — and we'll help you get set up.
          </p>
          <Link href="/contact">
            <Button
              size="lg"
              className="font-semibold px-10 mb-4"
              style={{ backgroundColor: INDIGO, color: "#ffffff" }}
              onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = "#5558e3")}
              onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = INDIGO)}
              data-testid="button-bottom-cta"
            >
              Make an Enquiry
            </Button>
          </Link>
          <p className="text-xs" style={{ color: DIMMED }}>
            Or email us directly at{" "}
            <a
              href="mailto:hello@vexistalentagency.com"
              className="transition-colors hover:text-white"
              style={{ color: MUTED }}
              data-testid="link-email"
            >
              hello@vexistalentagency.com
            </a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: "#020408", borderTop: "1px solid rgba(99,102,241,0.15)" }} className="text-white py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src={vexisLogo} alt="VEXIS Talent Agency" className="h-10 w-auto" />
            <p className="text-sm" style={{ color: DIMMED }}>
              Vexis — Recruitment assessment technology for the modern hiring workflow.
            </p>
            <div className="flex gap-6 text-sm" style={{ color: DIMMED }}>
              <Link href="/" className="hover:text-white transition-colors">Home</Link>
              <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link href="/about" className="hover:text-white transition-colors">About</Link>
              <Link href="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
